import React, { useCallback, useEffect, useState } from 'react';
import TopNav from '../../components/layout/TopNav';
import api from '../../services/api';

type Dimension = 'by-dealer' | 'by-region' | 'by-brand';

interface Bucket {
  bucketKey: string;
  bucketLabel: string;
  tenantCount: number;
  totalVideos: number;
  averageScore: number;
  complianceFlagRate: number;
}

interface LeaderboardEntry {
  tenantId: string;
  tenantName: string;
  dealerGroupId: string | null;
  dealerGroupName: string | null;
  totalVideos: number;
  averageScore: number;
  complianceFlagRate: number;
}

const DIMENSIONS: Array<{ id: Dimension; label: string; icon: string }> = [
  { id: 'by-dealer', label: 'By Dealer Group', icon: 'corporate_fare' },
  { id: 'by-region', label: 'By Region', icon: 'public' },
  { id: 'by-brand', label: 'By OEM Brand', icon: 'verified' },
];

const BenchmarkingPage: React.FC = () => {
  const [dimension, setDimension] = useState<Dimension>('by-dealer');
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bucketsRes, leaderboardRes] = await Promise.all([
        api.get(`/benchmarks/${dimension}`),
        api.get('/benchmarks/leaderboard?topN=20'),
      ]);
      setBuckets(bucketsRes.data.data ?? []);
      setLeaderboard(leaderboardRes.data.data ?? []);
    } catch {
      setBuckets([]);
      setLeaderboard([]);
    } finally {
      setLoading(false);
    }
  }, [dimension]);

  useEffect(() => {
    load();
  }, [load]);

  const maxVideos = Math.max(1, ...buckets.map((b) => b.totalVideos));
  const maxScore = Math.max(1, ...buckets.map((b) => b.averageScore));

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 flex flex-col transition-colors duration-300">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">
            Cross-Dealer Benchmarking
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            OEM network averages, region-wide performance, and leaderboard rankings
          </p>
        </div>

        {/* Dimension tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-slate-200 dark:border-slate-800">
          {DIMENSIONS.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setDimension(d.id)}
              className={`flex items-center gap-2 h-10 px-4 text-[10px] font-black uppercase tracking-widest transition-all border-b-2 -mb-px ${
                dimension === d.id
                  ? 'text-primary border-primary'
                  : 'text-slate-400 dark:text-slate-600 border-transparent hover:text-slate-600'
              }`}
            >
              <span className="material-symbols-outlined text-[16px]">{d.icon}</span>
              {d.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full" />
          </div>
        ) : (
          <>
            {/* Bucket comparison */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8 mb-8">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest mb-6">
                {DIMENSIONS.find((d) => d.id === dimension)?.label} Comparison
              </h2>
              {buckets.length === 0 ? (
                <p className="text-sm text-slate-400 dark:text-slate-600 py-8 text-center">
                  No data to compare for this dimension.
                </p>
              ) : (
                <div className="space-y-6">
                  {buckets.map((b) => (
                    <div key={b.bucketKey} className="space-y-2">
                      <div className="flex items-end justify-between">
                        <div>
                          <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                            {b.bucketLabel}
                          </h3>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            {b.tenantCount} accounts · {b.totalVideos} videos
                          </p>
                        </div>
                        <div className="flex items-center gap-6 text-right">
                          <div>
                            <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                              Avg Score
                            </div>
                            <div className="text-2xl font-bold text-primary tracking-tight">
                              {b.averageScore.toFixed(1)}
                            </div>
                          </div>
                          <div>
                            <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                              Flag Rate
                            </div>
                            <div className="text-lg font-bold text-amber-500">
                              {(b.complianceFlagRate * 100).toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden flex gap-0.5 p-0.5">
                        <div
                          className="h-full bg-primary rounded-full transition-all duration-1000"
                          style={{ width: `${(b.averageScore / maxScore) * 100}%` }}
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-1.5 bg-slate-50 dark:bg-slate-950 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-400 rounded-full"
                            style={{ width: `${(b.totalVideos / maxVideos) * 100}%` }}
                          />
                        </div>
                        <span className="text-[9px] font-black uppercase tracking-widest text-slate-300">
                          Volume
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Leaderboard */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest">
                  Top 20 Accounts
                </h2>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                  By Avg Composite Score
                </span>
              </div>
              {leaderboard.length === 0 ? (
                <p className="text-sm text-slate-400 dark:text-slate-600 py-8 text-center">
                  No leaderboard data yet.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-slate-800">
                      {['#', 'Account', 'Group', 'Videos', 'Avg Score', 'Flag Rate'].map((h) => (
                        <th
                          key={h}
                          className="px-3 py-3 text-left text-[10px] font-black uppercase tracking-widest text-slate-400"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((t, i) => (
                      <tr
                        key={t.tenantId}
                        className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors"
                      >
                        <td className="px-3 py-3 font-bold text-slate-900 dark:text-white text-xs">
                          {i + 1}
                        </td>
                        <td className="px-3 py-3 font-bold text-slate-900 dark:text-white text-xs">
                          {t.tenantName}
                        </td>
                        <td className="px-3 py-3 text-xs text-slate-500 dark:text-slate-400">
                          {t.dealerGroupName ?? '—'}
                        </td>
                        <td className="px-3 py-3 text-xs text-slate-500 dark:text-slate-400">
                          {t.totalVideos}
                        </td>
                        <td className="px-3 py-3 text-sm font-black text-primary">
                          {t.averageScore.toFixed(1)}
                        </td>
                        <td className="px-3 py-3 text-xs text-amber-500">
                          {(t.complianceFlagRate * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default BenchmarkingPage;
